import { createFileRoute } from "@tanstack/react-router";
import type { BlogArticle } from "@/lib/blog-types";
import { buildArticleJsonLd, JsonLd } from "@/lib/json-ld";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";

function apiUrl(path: string) {
	if (typeof window !== "undefined") return path;
	const baseUrl =
		import.meta.env.VITE_SITE_URL ||
		import.meta.env.VITE_BETTER_AUTH_URL ||
		"http://localhost:3000";
	return `${baseUrl}${path}`;
}

export const Route = createFileRoute("/blog/$slug")({
	loader: async ({ params }) => {
		if (import.meta.env.SSR) {
			const { getBlogArticleBySlug } = await import("@/lib/blog-server");
			const article = await getBlogArticleBySlug(params.slug);
			if (!article) {
				throw new Error("Blog article not found");
			}

			return { article };
		}

		const response = await fetch(
			apiUrl(`/api/blog/${encodeURIComponent(params.slug)}`),
		);
		if (!response.ok) {
			throw new Error("Failed to load blog article");
		}

		return response.json() as Promise<{ article: BlogArticle }>;
	},
	component: BlogArticlePage,
	head: ({ loaderData, matches, params }) => {
		const article = loaderData?.article;
		const path = `/blog/${params.slug}`;
		const seoUrls = getLocalizedSeoUrls({ matches, internalPath: path });
		const title = article
			? `AgentReacher | ${article.title}`
			: "AgentReacher | Blog";
		const description =
			article?.metaDescription ??
			"Guides and insights on AI-powered social scheduling.";
		const image = article?.imageUrl ?? ogImageUrl("blog");

		return {
			meta: [
				{ title },
				{
					name: "description",
					content: description,
				},
				{ property: "og:title", content: title },
				{
					property: "og:description",
					content: description,
				},
				{ property: "og:image", content: image },
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "article" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:title", content: title },
				{ name: "twitter:description", content: description },
				{ name: "twitter:image", content: image },
			],
			links: buildLocalizedSeoLinks({ matches, internalPath: path }),
		};
	},
});

function formatDate(value: string) {
	return new Intl.DateTimeFormat("en", {
		day: "numeric",
		month: "long",
		year: "numeric",
	}).format(new Date(value));
}

function BlogArticlePage() {
	const { article } = Route.useLoaderData();

	return (
		<article className="space-y-10">
			<JsonLd
				data={buildArticleJsonLd({
					datePublished: article.publishedAt,
					description: article.metaDescription ?? article.title,
					image: article.imageUrl ?? ogImageUrl("blog"),
					path: `/blog/${article.slug}`,
					title: article.title,
				})}
			/>
			<header className="space-y-4">
				<p className="text-xs text-muted-foreground">
					{formatDate(article.publishedAt)}
				</p>
				<h1 className="text-3xl font-medium tracking-tight sm:text-4xl">
					{article.title}
				</h1>
				{article.metaDescription ? (
					<p className="text-base text-muted-foreground">
						{article.metaDescription}
					</p>
				) : null}
				{article.imageUrl ? (
					<img
						src={article.imageUrl}
						alt=""
						className="mt-6 aspect-[16/9] w-full rounded-md object-cover"
					/>
				) : null}
			</header>
			<div
				className="blog-article-content"
				// biome-ignore lint/security/noDangerouslySetInnerHtml: Outrank payloads are authenticated and sanitized before storage.
				dangerouslySetInnerHTML={{ __html: article.contentHtml }}
			/>
		</article>
	);
}
